import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import cn from 'classnames';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import Spinner from '@/components/common/Spinner/Spinner';
import { useColumns } from '@/pages/ContractDetails/ContractLines/grid/combinedContractLinesColumns';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import ContractLinesGrid from './components/ContractLinesGrid/ContractLinesGrid';
import { clearLinesSelection, linesSelectionObservable }
  from './components/ContractLinesGrid/linesSelectionObservable';
import { useGridSelectedRows } from '@/pages/ContractLines/hooks/useGridSelectionSubscription';
import {
  clearVariableLinesSelection,
  variableLinesSelectionObservable,
} from './components/ContractLinesGrid/variableLinesSelectionObservable';
import useGetVariableContractLines from './hooks/useGetVariableContractLines';
import { VARIABLE_TYPE } from '@/config/statuses.config';
import {
  ContractLineBillingCycleOutput,
  VariableContractLineOutput,
  ContractLineBillingType
} from '@/graphql/contracts/_generated_/contracts';
import { useVariableColumns } from './grid/variableContractLinescolumns';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import useGetPaginatedBCContractLines from './hooks/useGetPaginatedBCContractLines';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import { useBulkUpdateContractLineInvoiceToCustomer } from './hooks/useBulkUpdateContractLineInvoiceToCustomer';
import { useBulkUpdateContractLineShipToCustomer } from './hooks/useBulkUpdateContractLineShipToCustomer';
import useGetPurchaseOrders from './hooks/useGetPurchaseOrders';
import { useBulkAssignPurchaseOrder } from './hooks/useBulkAssignPurchaseOrder';
import { useBulkUnassignPurchaseOrder } from './hooks/useBulkUnassignPurchaseOrder';
import LineBillingInfoModal from './components/LineBillingInfoModal/LineBillingInfoModal';
import PauseContractLineModal from
  '../RightPanel/Billing/PauseBilling/components/PauseContractLine/PauseContractLineModal';
import usePauseContractLine from
  '../RightPanel/Billing/PauseBilling/hooks/usePauseContractLine';
import { getReasonTypes } from '@/utils/data-helpers/reasonTypes';
import { PAUSE_BILLING_TYPE, DNR_TYPE } from '@/config/reasonTypes.config';
import DnrBulkUpdateModal from
  '../RightPanel/Statuses/components/DnrStatus/dnrBulkUpdateModal/DnrBulkUpdateModal';
import TerminateBulkUpdateModal from
  '../RightPanel/Statuses/components/TerminateStatus/terminateBulkUpdateModal/TerminateBulkUpdateModal';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { contractsBCPath } from '@/utils/path.utlis';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';

interface IContractLineContainer {
  contract: ContractCommonData | undefined
}

export default function ContractLinesContainer({ contract }: IContractLineContainer) {
  const history = useHistory();
  const { contractId }: { contractId: UUID } = useParams();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isRestrictedRole = isUserRole || isReaderRole || isContractOneEvoMigrated(contract);
  const contractHasVariableType = contract?.billingType?.includes(VARIABLE_TYPE);
  const [isLineBillingModalOpen, setIsLineBillingModalOpen] = useState(false);
  const [selectedLineBilling, setSelectedLineBilling] =
    useState<(ContractLineBillingType & { contractLineId: string; isVariableLines?: boolean }) | null>(null);
  const [isPauseContractLineModalOpen, setIsPauseContractLineModalOpen] = useState(false);
  const [selectedContractLineIdForPause, setSelectedContractLineIdForPause] = useState<string | null>(null);
  const [isSelectedLineVariable, setIsSelectedLineVariable] = useState(false);
  const [isDnrModalOpen, setIsDnrModalOpen] = useState(false);
  const [selectedDnrContractLineId, setSelectedDnrContractLineId] = useState<string | null>(null);
  const [isDnrLineVariable, setIsDnrLineVariable] = useState(false);
  const [isTerminatingModalOpen, setIsTerminatingModalOpen] = useState(false);
  const [selectedTerminatingContractLineId, setSelectedTerminatingContractLineId] = useState<string | null>(null);

  const reasonTypes = getReasonTypes();
  const {
    getReasonCodes,
    isLoading: isLoadingReasonCodes,
    reasonCodes
  } = useGetReasonCodes();

  const {
    getReasonCodes: getReasonCodesWithDisabled,
    reasonCodes: reasonCodesWithDisabled
  } = useGetReasonCodes();

  const {
    getReasonCodes: getDnrReasonCodes,
    isLoading: isLoadingDnrReasonCodes,
    reasonCodes: dnrReasonCodes
  } = useGetReasonCodes();

  const handleOpenPauseContractLineModal = (
    contractLine: ContractLineBillingCycleOutput | VariableContractLineOutput,
    isVariableLines?: boolean
  ) => {
    const lineId = 'id' in contractLine
      ? (contractLine as ContractLineBillingCycleOutput).id
      : (contractLine as VariableContractLineOutput).iD;
    setSelectedContractLineIdForPause(lineId);
    setIsSelectedLineVariable(isVariableLines ?? false);
    setIsPauseContractLineModalOpen(true);
    getReasonCodes(PAUSE_BILLING_TYPE);
    getReasonCodesWithDisabled(PAUSE_BILLING_TYPE, true);
  };

  const handleCloseLineBillingModal = () => {
    setIsLineBillingModalOpen(false);
    setSelectedLineBilling(null);
  };

  const handleClosePauseContractLineModal = () => {
    setIsPauseContractLineModalOpen(false);
    setSelectedContractLineIdForPause(null);
    setIsSelectedLineVariable(false);
  };

  const handleOpenDnrModal = (
    contractLine: ContractLineBillingCycleOutput | VariableContractLineOutput,
    isVariableLines?: boolean
  ) => {
    const lineId = 'id' in contractLine
      ? (contractLine as ContractLineBillingCycleOutput).id
      : (contractLine as VariableContractLineOutput).iD;
    setSelectedDnrContractLineId(lineId);
    setIsDnrLineVariable(isVariableLines ?? false);
    setIsDnrModalOpen(true);
    getDnrReasonCodes(DNR_TYPE);
  };

  const handleCloseDnrModal = () => {
    setIsDnrModalOpen(false);
    setSelectedDnrContractLineId(null);
    setIsDnrLineVariable(false);
  };

  const handleOpenTerminatingModal = (
    contractLine: ContractLineBillingCycleOutput,
  ) => {
    setSelectedTerminatingContractLineId(contractLine.id);
    setIsTerminatingModalOpen(true);
  };

  const handleCloseTerminatingModal = () => {
    setIsTerminatingModalOpen(false);
    setSelectedTerminatingContractLineId(null);
  };

  const {
    getPaginatedBCContractLines,
    isLoadingPaginatedBCContractLines,
    paginatedBCContractLines
  } = useGetPaginatedBCContractLines();

  const contractLines = useMemo(() =>
    paginatedBCContractLines?.map((el) => ({
      ...el,
      unitPrice: Number.parseFloat(el?.unitPrice ?? '0'),
      billingPeriodValue: Number.parseFloat(el?.billingPeriodValue ?? '0'),
      annualBillingPeriodValue: Number.parseFloat(el?.annualBillingPeriodValue ?? '0'),
    })).sort((a, b) => new Date(a?.created).getTime() - new Date(b?.created).getTime()),
  [paginatedBCContractLines]
  );

  const {
    getVariableContractLines,
    isLoadingVariableContractLines,
    variableContractLines
  } = useGetVariableContractLines();

  const transformedContractLineToSelectedLines = useMemo(() => {
    if (!selectedContractLineIdForPause) return [];

    let freshContractLine: ContractLineBillingCycleOutput | VariableContractLineOutput | null = null;

    if (isSelectedLineVariable) {
      freshContractLine = variableContractLines.find(
        (line) => line?.iD === selectedContractLineIdForPause
      ) as VariableContractLineOutput | null;
    } else {
      freshContractLine = paginatedBCContractLines?.find(
        (line) => line?.id === selectedContractLineIdForPause
      ) as ContractLineBillingCycleOutput | null;
    }

    if (!freshContractLine) return [];

    return [{
      id: selectedContractLineIdForPause,
      payload: {
        contractLine: freshContractLine
      }
    }];
  }, [selectedContractLineIdForPause, isSelectedLineVariable, paginatedBCContractLines, variableContractLines]);

  const freshDnrContractLine = useMemo(() => {
    if (!selectedDnrContractLineId) return null;

    if (isDnrLineVariable) {
      return (variableContractLines.find(
        (line) => line?.iD === selectedDnrContractLineId
      ) as VariableContractLineOutput) ?? null;
    }

    return (paginatedBCContractLines?.find(
      (line) => line?.id === selectedDnrContractLineId
    ) as ContractLineBillingCycleOutput) ?? null;
  }, [selectedDnrContractLineId, isDnrLineVariable, paginatedBCContractLines, variableContractLines]);

  const freshTerminatingContractLine = useMemo(() => {
    if (!selectedTerminatingContractLineId) return null;

    return (paginatedBCContractLines?.find(
      (line) => line?.id === selectedTerminatingContractLineId
    ) as ContractLineBillingCycleOutput) ?? null;
  }, [selectedTerminatingContractLineId, paginatedBCContractLines]);

  const contractLinesColumns = useColumns(
    handleOpenPauseContractLineModal,
    handleOpenDnrModal,
    handleOpenTerminatingModal
  );

  const searchShipToCustomer = useSearchCustomers();
  const searchInvoiceToCustomer = useSearchCustomers();
  const searchPurchaseOrders = useGetPurchaseOrders();
  const { bulkUpdateContractLineShipToCustomer } = useBulkUpdateContractLineShipToCustomer(contractId);
  const { bulkUpdateContractLineInvoiceToCustomer } = useBulkUpdateContractLineInvoiceToCustomer(contractId);
  const { bulkAssignPurchaseOrder } = useBulkAssignPurchaseOrder(true, contractId, true);
  const { bulkUnassignPurchaseOrder } = useBulkUnassignPurchaseOrder(true, contractId, true);
  const { pauseContractLine } = usePauseContractLine();

  const variableContractLinesColumns = useVariableColumns({
    searchShipToCustomer,
    searchInvoiceToCustomer,
    searchPurchaseOrders,
    bulkUpdateContractLineShipToCustomer,
    bulkUpdateContractLineInvoiceToCustomer,
    bulkAssignPurchaseOrder,
    bulkUnassignPurchaseOrder,
    onOpenPauseContractLineModal: handleOpenPauseContractLineModal,
    onOpenDnrModal: handleOpenDnrModal,
    userRole
  });

  const gridLinesLoaded = !isLoadingVariableContractLines && !isLoadingPaginatedBCContractLines;
  const bothGridsPresent = variableContractLines.length > 0 && contractLines.length > 0;
  const rowIndex = new URL(window.location.href).searchParams.get('rowIndex') ?? '';
  const rowIndexQuery = `?rowIndex=${rowIndex}`;
  const addContractLinesCallback = (event: MouseEvent) => {
    event.preventDefault();
    const link = getParameterizedLink(
      `/${contractsBCPath}/${contractId}/add-contract-line${rowIndexQuery}`
    );
    history.push(link);
  };

  const addVariableContractLinesCallback = (event: MouseEvent) => {
    event.preventDefault();
    const link = getParameterizedLink(
      `/${contractsBCPath}/${contractId}/add-variable-contract-line${rowIndexQuery}`
    );
    history.push(link);
  };

  const selectedContractLines = useGridSelectedRows(linesSelectionObservable);
  const selectedVariablesContractLines = useGridSelectedRows(variableLinesSelectionObservable);

  const contractLinesGrid = useMemo(() => {
    const cls = cloneDeep(contractLines ?? []);

    return (
      <ContractLinesGrid
        contractLines={cls as unknown as ContractLineBillingCycleOutput[]}
        columns={contractLinesColumns}
        obsarvable={linesSelectionObservable}
        currency={contract?.currency.currency}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractLines]);

  const variableContractLinesGrid = useMemo(() => {
    const cls = cloneDeep(variableContractLines ?? [])
      .sort((a, b) => new Date(a?.created).getTime() - new Date(b?.created).getTime());

    return (
      <ContractLinesGrid
        contractLines={cls as VariableContractLineOutput[]}
        columns={variableContractLinesColumns}
        obsarvable={variableLinesSelectionObservable}
        currency={contract?.currency.currency}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableContractLines]);

  useEffect(() => {
    getPaginatedBCContractLines({ contractId: { eq: contractId } });

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [contractId]);

  useEffect(() => {
    if (contract?.billingType?.includes(VARIABLE_TYPE)) {
      getVariableContractLines({ headerPrimaryKey: { eq: contractId } });
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [contract?.contractID]);

  if (isLoadingVariableContractLines || isLoadingPaginatedBCContractLines) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
        <Spinner />
      </div>
    );
  }

  if (gridLinesLoaded && !contractLines?.length && !variableContractLines?.length) {
    return (
      <div className="d-flex justify-content-center">
        <p className="mt-20 mb-40 c-title">
          This contract does not have any contract lines yet
        </p>
      </div>
    );
  }

  return (
    <>
      {contractLines?.length > 0 && (
        <SectionCard
          title={{
            titleText: 'Contract Lines',
            additionalText: `${selectedContractLines.length} Lines Selected`
          }}
          collapsable={{
            buttonText: 'Contract Lines',
            onOpenCallback: clearLinesSelection,
          }}
          actions={isRestrictedRole ? [] : [{
            buttonText: 'Add Contract Lines',
            callback: addContractLinesCallback
          }, {
            buttonText: 'Add Variable Contract Lines',
            callback: addVariableContractLinesCallback
          }]}
          className={cn({ 'mb-25': variableContractLines?.length })}
        >
          {contractLines?.length ? (
            contractLinesGrid
          ) : null}
        </SectionCard>
      )}

      {variableContractLines?.length > 0 && contractHasVariableType && (
        <SectionCard
          title={{
            titleText: 'Variable Contract Lines',
            additionalText: `${selectedVariablesContractLines.length} Lines Selected`
          }}
          collapsable={{
            buttonText: 'Variable Contract Lines',
            onOpenCallback: clearVariableLinesSelection,
          }}
          actions={!bothGridsPresent && !isRestrictedRole ? [{
            buttonText: 'Add Contract Lines',
            callback: addContractLinesCallback
          },
          {
            buttonText: 'Add Variable Contract Lines',
            callback: addVariableContractLinesCallback
          }] : undefined}
        >
          {variableContractLines?.length ? (
            variableContractLinesGrid
          ) : null}
        </SectionCard>
      )}

      <LineBillingInfoModal
        isOpen={isLineBillingModalOpen}
        onClose={handleCloseLineBillingModal}
        contractLine={selectedLineBilling ? {
          id: selectedLineBilling.contractLineId,
          lineBillingStatus: selectedLineBilling.status,
          lineBillingCurrentStatusEndDate: selectedLineBilling.currentStatusEndDate,
          lineBillingStatusUpdateComment: selectedLineBilling.statusUpdateComment,
          lineBillingStatusUpdateReasonCode: selectedLineBilling.statusUpdateReasonCode,
          lineBillingStatusUpdateReference: selectedLineBilling.statusUpdateReference,
          lineBillingStatusUpdated: selectedLineBilling.statusUpdated
        } as any : null}
      />

      {selectedContractLineIdForPause
        && isPauseContractLineModalOpen
        && transformedContractLineToSelectedLines.length > 0 && (
          <PauseContractLineModal
            onClose={handleClosePauseContractLineModal}
            pauseContractLine={pauseContractLine}
            reasonCodes={reasonCodes}
            reasonTypes={reasonTypes}
            selectedLines={isSelectedLineVariable ? [] : transformedContractLineToSelectedLines}
            selectedVariableLines={isSelectedLineVariable ? transformedContractLineToSelectedLines : []}
            isLoadingReasonCodes={isLoadingReasonCodes}
            reasonCodesWithDisabled={reasonCodesWithDisabled}
            readOnly={isRestrictedRole}
          />
      )}

      {isDnrModalOpen && freshDnrContractLine && contract && (
        <DnrBulkUpdateModal
          dnrContractLines={[freshDnrContractLine] as any}
          onClose={handleCloseDnrModal}
          contract={contract as any}
          reasonCodes={dnrReasonCodes}
          reasonTypes={reasonTypes}
          isLoadingReasonCodes={isLoadingDnrReasonCodes}
          readOnly={isRestrictedRole}
        />
      )}

      {isTerminatingModalOpen && freshTerminatingContractLine && contract && (
        <TerminateBulkUpdateModal
          terminateContractLines={[freshTerminatingContractLine]}
          onClose={handleCloseTerminatingModal}
          contract={contract as any}
          readOnly={isRestrictedRole}
        />
      )}
    </>
  );
}
